import { Box } from "@mui/material";
import React, { useMemo } from "react";
import { DeviationInfo } from "../../helpers/intakeHelpers";
import { calculateMealDeclarations } from "../../helpers/mealCalculationHelpers";
import { calculateAvailableMealTypes } from "../../helpers/planSelectors";
import {
  NutritionDeclarationType,
  PlanMealType,
} from "../../model/nutritionPlanTypes";
import IntakeDeviationMessage from "./IntakeDeviationMessage";
import MultiMealSegmentedProgress from "./MultiMealSegmentedProgress";
import TargetComparison from "./TargetComparison";

type Props = {
  mealDataCurrent: NutritionDeclarationType;
  mealDataOriginal: NutritionDeclarationType;
  dailyDataCurrent: NutritionDeclarationType;
  isSkipped: boolean;
  allMeals: PlanMealType[];
  currentMealType: number;
  deviation: DeviationInfo;
};

/**
 * The app's `MealIntakeView`: a calorie ring and three macro rings segmented
 * by meal with this meal highlighted, the deviation message, and — unless the
 * meal is skipped — the target comparison for whatever deviates.
 */
const MealIntakeView = ({
  mealDataCurrent,
  mealDataOriginal,
  dailyDataCurrent,
  isSkipped,
  allMeals,
  currentMealType,
  deviation,
}: Props) => {
  const allMealsData = useMemo(
    () =>
      calculateAvailableMealTypes(allMeals).flatMap((type) => {
        const meal = allMeals.find((entry) => entry.type === type.type);
        if (!meal) return [];
        const { dataCurrent } = calculateMealDeclarations(meal);
        return [
          {
            mealType: type.type,
            label: type.label,
            calories: dataCurrent.cals,
            carbs: dataCurrent.carbs,
            protein: dataCurrent.protein,
            fat: dataCurrent.fat,
          },
        ];
      }),
    [allMeals]
  );

  const segments = (key: "calories" | "carbs" | "protein" | "fat") =>
    allMealsData.map((meal) => ({
      mealType: meal.mealType,
      value: meal[key],
      label: meal.label,
    }));

  return (
    <Box sx={styles.container}>
      <Box sx={styles.calories}>
        <MultiMealSegmentedProgress
          allMealsData={segments("calories")}
          currentMealType={currentMealType}
          dailyTotal={dailyDataCurrent.cals}
          label="kcal"
          nutrientType="calories"
          size={120}
          title=""
        />
      </Box>
      <Box sx={styles.macros}>
        <MultiMealSegmentedProgress
          allMealsData={segments("carbs")}
          currentMealType={currentMealType}
          dailyTotal={dailyDataCurrent.carbs}
          label="gr"
          nutrientType="carbs"
          size={90}
          title="Carbs"
        />
        <MultiMealSegmentedProgress
          allMealsData={segments("protein")}
          currentMealType={currentMealType}
          dailyTotal={dailyDataCurrent.protein}
          label="gr"
          nutrientType="protein"
          size={90}
          title="Protein"
        />
        <MultiMealSegmentedProgress
          allMealsData={segments("fat")}
          currentMealType={currentMealType}
          dailyTotal={dailyDataCurrent.fat}
          label="gr"
          nutrientType="fat"
          size={90}
          title="Fat"
        />
      </Box>
      <IntakeDeviationMessage
        deviationType={deviation.type ?? "increased"}
        show={deviation.hasDeviation}
      />
      {!isSkipped && (
        <TargetComparison
          current={mealDataCurrent}
          original={mealDataOriginal}
          mode="deviationsOnly"
        />
      )}
    </Box>
  );
};

export default MealIntakeView;

const styles = {
  container: {
    display: "flex",
    flexDirection: "column" as "column",
    gap: "24px",
    paddingTop: "24px",
  },
  calories: {
    display: "flex",
    justifyContent: "center",
  },
  macros: {
    display: "flex",
    justifyContent: "space-around",
    padding: "0 8px 16px",
  },
};
